// Need to use the React-specific entry point to allow generating React hooks
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'

export type SuggestedQuestionsInput = {
  userContent: string
  assistantContent: string
}

type SuggestedQuestionsResponse = {
  data: {
    suggestions: {
      suggestion: string
      suggestion_number: number
    }[]
  }
}

// Define a service using a base URL and expected endpoints
export const paxtonAppApi = createApi({
  reducerPath: 'paxtonAppApi',
  baseQuery: fetchBaseQuery({
    baseUrl: kPaxtonAppApiRESTBaseUrl(),
    prepareHeaders: async (headers) => {
      // Add the auth token
      const token = await getAuth().currentUser?.getIdToken()
      if (token) {
        headers.set(`Authorization`, `Bearer ${token}`)
      }

      // Insert a Sentry transaction_id for the server to use
      const sentry_transaction_id = nanoid()
      headers.set('X-Transaction-Id', sentry_transaction_id)

      // Add the Content Type
      headers.set('Content-type', 'application/json; charset=UTF-8')

      return headers
    },
  }),

  endpoints: (builder) => ({
    getSuggestedQuestions: builder.query<SuggestedQuestionsResponse, SuggestedQuestionsInput>({
      query: (args) => ({
        url: 'api/v1/suggestion',
        method: 'POST',

        // Format into the API's required format
        body: JSON.stringify({
          messages: [
            { role: 'user', content: args.userContent },
            { role: 'assistant', content: args.assistantContent },
          ],
        }),
      }),
      keepUnusedDataFor: 3600, // 1 hour - this does not need to be refetched often if the inputs do not change

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, _) => {
        // If there's a response, get it as JSON
        const response = meta?.response
        const body = response?.body
        console.log(`Fetch error - status: ${response?.status}, detail: ${body}`)

        // Get the sentry transaction id from the response header
        const transaction_id = response?.headers.get('X-Transaction-Id')

        // Report to sentry except for rate limit errors
        if (response?.status !== 429) {
          Sentry.withScope((scope) => {
            if (typeof transaction_id == 'string') {
              scope.setTags({ transaction_id: transaction_id })
            } else {
              console.error('X-Transaction-Id missing in response headers: ', response?.headers)
            }
            Sentry.captureException(new Error(`Fetch error - status: ${response?.status}, detail: ${body}`), {
              extra: {
                request: meta?.request,
                response: response,
                onLine: navigator.onLine,
                cookieEnabled: navigator.cookieEnabled,
                baseQueryReturnValueStatus: baseQueryReturnValue.status,
                baseQueryReturnValueData: baseQueryReturnValue.data,
              },
            })
          })
        }

        return baseQueryReturnValue
      },
    }),
  }),
})

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const { useGetSuggestedQuestionsQuery } = paxtonAppApi
